import { useGameStore } from '@/stores/gameStore.ts'
import { icons } from '@/assets/icons.ts'
import { techIcons } from '@/assets/techIcons.ts'

class FerionCDN {
  private _gameStore?: ReturnType<typeof useGameStore>

  private get gameStore() {
    if (!this._gameStore) {
      this._gameStore = useGameStore() // Initialize the store when accessed
    }
    return this._gameStore
  }

  spy(size: number, imageCode: number): string {
    return this.gameStore.cdnUrl + 'images/spies/' + size + '/spy_' + imageCode + '.webp'
  }
  spyDead(size: number, imageCode: number): string {
    return (
      this.gameStore.cdnUrl + 'images/spies/grayscale/' + size + '/spy_' + imageCode + '.webp'
    )
  }
  spyVideo(imageCode: number): string {
    return this.gameStore.cdnUrl + 'videos/spies/spy_' + imageCode + '.webm'
  }

  technology(size: number, type: string): string {
    return this.gameStore.cdnUrl + 'images/technologies/' + size + '/' + type + '.webp'
  }

  building(size: number, type: string): string {
    return this.gameStore.cdnUrl + 'images/buildings/' + size + '/' + type + '.webp'
  }

  buildingVideo(size:number, type: string): string {
    return this.gameStore.cdnUrl + 'videos/buildings/' + size + '/' + type + '.webm'
  }

  ship(size: number): string {
    return this.gameStore.cdnUrl + 'images/buildings/' + size + '/ship.webp'
  }

  shipComponent(size: number, type: string): string {
    return this.gameStore.cdnUrl + 'images/shipComponents/' + size + '/' + type + '.webp'
  }

  help(section: string, image: string): string {
    return this.gameStore.cdnUrl + 'help/' + section + '/' + image + '.webp'
  }

  planet(size: number, planetSize: number, type: number | string): string {
    return (
      this.gameStore.cdnUrl +
      'images/planets/' +
      type +
      '/' +
      planetSize +
      '/' +
      size +
      '.webp'
    )
  }

  planetShield(): string {
    return this.gameStore.cdnUrl + 'sprites/battlereport/shield.png'
  }

  attributeSide(type: string): string {
    return this.gameStore.cdnUrl + 'images/attributes/' + type + '/side.webp'
  }

  interface(size: number, type: string): string {
    return this.gameStore.cdnUrl + 'images/interface/' + type + '/' + size + '.webp'
  }

  icon32(type: string): string {
    return this.gameStore.cdnUrl + 'images/icons/32/' + type + '.webp'
  }

  icon(type: string): string {
    return icons[this.normalize(type)] || '' // Return an empty string if the type is not found
  }

  ore(type: string | number, size: number): string {
    return this.gameStore.cdnUrl + 'images/ore/' + type + '/' + size + '.webp'
  }

  techIcon(type: string): string {
    return techIcons[type] || '' // Return an empty string if the type is not found
  }

  ministerImage(code: string): string {
    return this.gameStore.cdnUrl + 'images/ministers/' + code + '.webp'
  }
  ministerVideo(code: string): string {
    return this.gameStore.cdnUrl + 'videos/ministers/' + code + '_looped.webm'
  }

  countryFlag(countryCode: string | null | undefined, size: '16x12' | '20x15' | '24x18' | '28x21' | '32x24' | '36x27' | '40x30' | '48x36' | '56x42' | '60x45' | '64x48' | '72x54' | '80x60' | '84x63' | '96x72' | '112x84' | '120x90' | '128x96' | '144x108' | '160x120' | '192x144' | '224x168' | '256x192' = '32x24'): string {
    // Use a black/pirate flag SVG for null, undefined, empty, or "??" country codes
    if (!countryCode || countryCode === '' || countryCode === '??') {
      // Using a data URI with a simple black flag SVG
      return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2NDAgNDgwIj48cmVjdCB3aWR0aD0iNjQwIiBoZWlnaHQ9IjQ4MCIgZmlsbD0iIzAwMDAwMCIvPjwvc3ZnPg=='
    }
    
    return `https://flagcdn.com/${size}/${countryCode.toLowerCase()}.webp`
  }

  normalize(type: string): string {
    const replaceList: Record<string, string> = {
      defense_attack_ships: 'attack_against_ships',
      attack_against_ships: 'attack_against_ships',
      defence_attack_ships: 'attack_against_ships',
      defence_shields: 'shields',
      ore_refinery: 'refinery',
      ship_engine_thrust: 'thrust',
      ship_engine_max_speed: 'max_speed',
      ship_max_speed: 'max_speed',
      ship_hit_points: 'hit_points',
      ship_shields: 'shields',
      ship_attack_ships: 'attack_against_ships',
      ship_attack_planets: 'attack_against_planets',
      ship_repair: 'repair',
      ship_frame: 'frame',
      ship_mass: 'mass',
      ship_scanning_range: 'scanning_range',
      ship_production_cost: 'production',
      armor: 'hit_points',
      weapon: 'attack_against_ships',
      engine: 'max_speed',
      scanner: 'scanning_range',
      special: 'cog',
      shield: 'shields',
      weapon_SHIPS: 'attack_against_ships',
      weapon_PLANETS: 'attack_against_planets'
    }

    return replaceList[type] || type
  }
}

export const CDN = new FerionCDN()
